import React, {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Popover,
  Grid,
  SxProps,
  Theme,
  Alert,
  Paper,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import { stripAllFormatting } from './utils';
const API_URL = import.meta.env.VITE_API_URL;

const EMOJIS = [
  '😀',
  '😂',
  '😍',
  '😎',
  '😊',
  '👍',
  '🙏',
  '💪',
  '🔥',
  '🎉',
  '🤔',
  '😉',
  '😢',
  '😡',
  '🥳',
  '💖',
  '🌟',
  '🍕',
  '🍔',
  '☕',
  '🍰',
  '🧁',
  '🍩',
  '🍫',
  '🍬',
  '🍪',
  '🍭',
  '🍎',
  '🍓',
  '🍇',
  '🍉',
  '🍌',
  '🍒',
  '✨',
  '⭐',
  '🌠',
  '🇺🇦',
  '👋🏻',
];

export interface MacrosEditorHandle {
  insertText: (text: string) => void;
}

interface MacrosEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  minRows?: number;
  sx?: SxProps<Theme>;
  onFocus?: () => void;
}

export const MacrosEditor = forwardRef<MacrosEditorHandle, MacrosEditorProps>(
  ({ value, onChange, label, minRows = 4, sx, onFocus }, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const savedRange = useRef<Range | null>(null);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [selectionRange, setSelectionRange] = useState<Range | null>(null);
    const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);

    useEffect(() => {
      if (editorRef.current && editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }, [value]);

    const saveRange = () => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        savedRange.current = sel.getRangeAt(0);
      }
    };

    const restoreRange = () => {
      const sel = window.getSelection();
      if (savedRange.current && sel) {
        sel.removeAllRanges();
        sel.addRange(savedRange.current);
      }
    };

    useImperativeHandle(ref, () => ({
      insertText: (text: string) => {
        editorRef.current?.focus();
        restoreRange();
        document.execCommand('insertText', false, text);
        triggerChange();
      },
    }));

    const triggerChange = () => {
      if (editorRef.current) onChange(editorRef.current.innerHTML);
    };

    const format = (command: string, val?: string) => {
      document.execCommand(command, false, val);
      triggerChange();
    };

    const handleEmojiClick = (emoji: string) => {
      format('insertText', emoji);
      setEmojiAnchor(null);
    };

    const handleCleanClick = () => {
      const cleaned = stripAllFormatting(editorRef.current?.innerHTML || '');
      if (editorRef.current) editorRef.current.innerHTML = cleaned;
      onChange(cleaned);
    };

    const handleLinkClick = () => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) setSelectionRange(sel.getRangeAt(0));
      setLinkDialogOpen(true);
    };

    const insertLink = () => {
      if (!selectionRange) return;
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(selectionRange);
      document.execCommand('createLink', false, linkUrl);
      triggerChange();
      setLinkDialogOpen(false);
      setLinkUrl('');
    };

    return (
      <Box sx={sx}>
        {label && (
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
            {label}
          </Typography>
        )}

        <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            onClick={() => format('bold')}
          >
            B
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => format('italic')}
          >
            I
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => format('underline')}
          >
            U
          </Button>
          <Button size="small" variant="outlined" onClick={handleLinkClick}>
            Посилання
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => setEmojiAnchor(e.currentTarget)}
          >
            😃
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            onClick={handleCleanClick}
            title="Очистити форматування та зайві переноси рядків"
            sx={{ minWidth: 0, px: 1 }}
          >
            <FormatClearIcon fontSize="small" />
          </Button>
        </Stack>

        <Popover
          open={Boolean(emojiAnchor)}
          anchorEl={emojiAnchor}
          onClose={() => setEmojiAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 1 }}>
            <Grid container spacing={0.5}>
              {EMOJIS.map((emoji) => (
                <Grid size={{ xs: 0.5 }} key={emoji}>
                  <Button
                    onClick={() => handleEmojiClick(emoji)}
                    sx={{ minWidth: 0, padding: 0.5, fontSize: 20 }}
                  >
                    {emoji}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Popover>

        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={triggerChange}
          onFocus={onFocus}
          onKeyUp={saveRange}
          onMouseUp={saveRange}
          sx={{
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 1,
            minHeight: minRows * 24,
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        />

        <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)}>
          <DialogTitle>Вставити посилання</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="URL"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLinkDialogOpen(false)}>Відмінити</Button>
            <Button variant="contained" onClick={insertLink}>
              Вставити
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  },
);

const MACROS_LABELS: Record<string, string> = {
  '${product.name}': 'Назва товару',
  '${product.description}': 'Опис',
  '${product.characteristic}': 'Характеристики',
  '${product.compose}': 'Склад',
  '${product.price}': 'Ціна',
  '${product.promoPrice}': 'Акційна ціна',
  '${product.quantity}': 'Кількість',
  '${product.totalPrice}': 'Сума',
  '${product.extraField_1}': 'Додаткове поле 1',
  '${product.extraField_2}': 'Додаткове поле 2',
  '${product.extraField_3}': 'Додаткове поле 3',
  '${users.firstName}': "Ім'я",
  '${users.lastName}': 'Прізвище',
  '${users.phone}': 'Телефон',
  '${users.city}': 'Місто',
  '${orders.number}': 'Номер замовлення',
  '${orders.date}': 'Дата замовлення',
  '${orders.status}': 'Статус',
  '${orders.city}': 'Місто замовлення',
  '${orders.fullName}': "Ім'я та прізвище",
  '${orders.deliveryDate}': 'Дата доставки',
  '${orders.shop}': 'Магазин',
  '${orders.totalPrice}': 'Сума замовлення',
  '${carts.totalPrice}': 'Сума кошика',
  '{{products}}': '▶ Початок блоку товарів',
  '{{/products}}': '◀ Кінець блоку товарів',
};

function MacrosPreview({ value }: { value: string }) {
  const rendered = value.replace(/(\$\{[\w.]+\}|\{\{\/?\w+\}\})/g, (match) => {
    const label = MACROS_LABELS[match];
    if (!label) return match;
    return `<span style="display:inline-block;background:#e3f2fd;color:#1565c0;border-radius:4px;padding:0 6px;font-size:12px;font-weight:500;margin:0 1px">${label}</span>`;
  });

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 0.5, display: 'block' }}
      >
        Попередній перегляд
      </Typography>
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          p: 1,
          minHeight: 60,
          whiteSpace: 'pre-wrap',
          fontSize: 13,
          color: 'text.primary',
          background: '#fafafa',
        }}
        dangerouslySetInnerHTML={{ __html: rendered }}
      />
    </Box>
  );
}

const MACROS_GROUPS = [
  {
    label: 'Товари',
    items: [
      { label: 'Назва товару', value: '${product.name}' },
      { label: 'Опис', value: '${product.description}' },
      { label: 'Характеристики', value: '${product.characteristic}' },
      { label: 'Склад', value: '${product.compose}' },
      { label: 'Ціна', value: '${product.price}' },
      { label: 'Акційна ціна', value: '${product.promoPrice}' },
      { label: 'Кількість', value: '${product.quantity}' },
      { label: 'Сума', value: '${product.totalPrice}' },
      { label: 'Додаткове поле 1', value: '${product.extraField_1}' },
      { label: 'Додаткове поле 2', value: '${product.extraField_2}' },
      { label: 'Додаткове поле 3', value: '${product.extraField_3}' },
    ],
  },
  {
    label: 'Користувач',
    items: [
      { label: "Ім'я", value: '${users.firstName}' },
      { label: 'Прізвище', value: '${users.lastName}' },
      { label: 'Телефон', value: '${users.phone}' },
      { label: 'Місто', value: '${users.city}' },
    ],
  },
  {
    label: 'Замовлення',
    items: [
      { label: 'Номер замовлення', value: '${orders.number}' },
      { label: 'Дата замовлення', value: '${orders.date}' },
      { label: 'Статус', value: '${orders.status}' },
      { label: 'Місто', value: '${orders.city}' },
      { label: "Ім'я та прізвище", value: '${orders.fullName}' },
      { label: 'Дата доставки', value: '${orders.deliveryDate}' },
      { label: 'Магазин', value: '${orders.shop}' },
      { label: 'Сума замовлення', value: '${orders.totalPrice}' },
    ],
  },
  {
    label: 'Кошик',
    items: [{ label: 'Сума кошика', value: '${carts.totalPrice}' }],
  },
  {
    label: 'Блок товарів',
    items: [
      { label: 'Початок блоку', value: '{{products}}' },
      { label: 'Кінець блоку', value: '{{/products}}' },
    ],
  },
];

const TEMPLATES = [
  { key: 'macros_product_card', label: 'Шаблон №1: Шаблон картки товару' },
  {
    key: 'macros_product_card_search',
    label: 'Шаблон №2: Шаблон картки товару при пошуку',
  },
  { key: 'macros_product_cart', label: 'Шаблон №3: Шаблон кошику' },
  { key: 'macros_order_list', label: 'Шаблон №4: Шаблон замовлення' },
  { key: 'macros_history', label: 'Шаблон №5: Шаблон історії замовлень' },
];

const REQUIRED_MACROS: Record<string, string[]> = {
  macros_product_card: ['${product.name}'],
  macros_product_card_search: ['${product.name}'],
  macros_product_cart: ['{{products}}', '{{/products}}', '${carts.totalPrice}'],
  macros_order_list: ['{{products}}', '{{/products}}', '${orders.number}'],
};

type MacrosData = Record<string, string>;

export default function ModuleMacros() {
  const [isMacrosOn, setIsMacrosOn] = useState<boolean | null>(null);
  const [data, setData] = useState<MacrosData>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeEditorKey, setActiveEditorKey] = useState<string>(
    'macros_product_card',
  );

  const editorRefs = useRef<Record<string, MacrosEditorHandle | null>>({});

  useEffect(() => {
    fetch(`${API_URL}/settings`)
      .then((r) => r.json())
      .then((res) => setIsMacrosOn(res.settings.isMacrosOn))
      .catch(() => setIsMacrosOn(false));

    fetch(`${API_URL}/macros`)
      .then((r) => r.json())
      .then((res) => {
        const map: MacrosData = {};
        res.data.forEach((m: any) => {
          map[m.type] = m.macros;
        });
        setData(map);
      })
      .finally(() => setLoading(false));
  }, []);

  const htmlToTelegramText = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;

    const walk = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return '';

      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();
      const inner = Array.from(el.childNodes).map(walk).join('');

      if (tag === 'b' || tag === 'strong') return `<b>${inner}</b>`;
      if (tag === 'i' || tag === 'em') return `<i>${inner}</i>`;
      if (tag === 'u') return `<u>${inner}</u>`;
      if (tag === 'a')
        return `<a href="${el.getAttribute('href')}">${inner}</a>`;
      if (tag === 'br') return '\n';
      if (tag === 'div' || tag === 'p') {
        const prefix = el.previousSibling ? '\n' : '';
        return prefix + inner;
      }

      return inner;
    };

    return walk(div).trim();
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);
    setSaving(true);
    try {
      const cleanData: MacrosData = {};
      for (const [key, value] of Object.entries(data)) {
        cleanData[key] = htmlToTelegramText(value);
      }
      await fetch(`${API_URL}/macros`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanData),
      });
      setSaveSuccess(true);
    } catch {
      setSaveError('Помилка при збереженні');
    } finally {
      setSaving(false);
    }
  };

  const handleMacrosClick = (macroValue: string) => {
    editorRefs.current[activeEditorKey]?.insertText(macroValue);
  };

  if (loading)
    return (
      <Box p={3}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box p={3}>
      <Typography variant="h6" mb={3}>
        Макроси
      </Typography>

      <Alert
        severity={isMacrosOn ? 'success' : 'error'}
        icon={isMacrosOn ? <CheckCircleIcon /> : <CancelIcon />}
        sx={{ mb: 3 }}
      >
        {isMacrosOn ? (
          <>
            Макроси підключені та готові до роботи. Зауважте, що при
            використанні макросів{' '}
            <b>
              налаштування модулів картки товару, картки замовлення та картки
              кошику працювати не будуть
            </b>
            .
          </>
        ) : (
          <>
            Макроси не підключені. Після підключення макросів модулі карток
            товару, замовлення та кошику працювати не будуть. Для використання
            макросів увімкніть макроси у{' '}
            <a href="/settings" style={{ color: 'inherit' }}>
              налаштуваннях
            </a>
            .
          </>
        )}
      </Alert>

      {isMacrosOn && (
        <>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Збережено успішно
            </Alert>
          )}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems="flex-start"
          >
            <Box flex={1}>
              <Stack spacing={3}>
                {TEMPLATES.map(({ key, label }) => (
                  <Box key={key}>
                    <MacrosEditor
                      key={key}
                      ref={(el) => {
                        editorRefs.current[key] = el;
                      }}
                      label={label}
                      value={data[key] ?? ''}
                      onChange={(html) =>
                        setData((prev) => ({ ...prev, [key]: html }))
                      }
                      onFocus={() => setActiveEditorKey(key)}
                      minRows={5}
                    />
                    <MacrosPreview value={data[key] ?? ''} />
                  </Box>
                ))}
              </Stack>

              <Button
                variant="contained"
                sx={{ mt: 3 }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Зберегти'}
              </Button>
            </Box>

            <Paper
              sx={{
                p: 2,
                minWidth: 280,
                maxWidth: 320,
                position: 'sticky',
                top: 16,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                Доступні макроси
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: 'block' }}
              >
                Натисніть на макрос щоб вставити його в активний шаблон
              </Typography>

              <Stack spacing={2}>
                {MACROS_GROUPS.map((group) => (
                  <Box key={group.label}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ mb: 1, display: 'block' }}
                    >
                      {group.label}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {group.items.map((item) => (
                        <Chip
                          key={item.value}
                          label={item.label}
                          size="small"
                          onClick={() => handleMacrosClick(item.value)}
                          sx={{ cursor: 'pointer', fontSize: 11 }}
                        />
                      ))}
                    </Stack>
                    <Divider sx={{ mt: 1.5 }} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </>
      )}
    </Box>
  );
}
